import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import {ReactComponent as Magnifer} from "../../assets/img/searchOutlined.svg"
import { RightOutlined } from "@ant-design/icons";
import FaqModal from "../../components/FaqModal/FaqModal";
import Spinner from "../../components/UIKits/Spinner/Spinner";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import CustomInput from "../../components/UIKits/CustomInput/CustomInput";
import { localStorageKeys } from "../../components/Utils/constants";
import { getSavedState } from "../../services/localStorage";
import { getProductFaqs } from "../../services/faq.service";

type FaqCategoryType = {
  id: string;
  title: string;
  content: string;
  categoryId: string;
  categoryName: string;
  productId: string;
  productName: string;
  status: number;
  statusName: string;
  createdBy: string;
  updatedBy: null;
  dateCreated: string;
  dateUpdated: null;
};

const Faq = () => {
  const params = {
    pageSize: 0,
    pageNumber: 0,
    parameter: { productName: "Charter", status: 2 },
  };
  const { data: user } = getSavedState(localStorageKeys.EmsUser);
  const fullName = user?.fullName || "";
  const firstName = fullName?.split(" ")[0] || "";
  const [activeFaq, setActiveFaq] = useState<FaqCategoryType | null>(null);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const searchText = watch("searchText");

  const {
    data: productFaqs,
    error: productFaqsError,
    isLoading: productFaqsLoading,
  } = useQuery({
    queryKey: ["productFaqs", params],
    queryFn: getProductFaqs,
    enabled: true,
    retry: false,
  });

  // Group FAQs by categoryName
  const groupFAQsByCategory = (
    faqs: FaqCategoryType[] | null
  ): Record<string, FaqCategoryType[]> | null => {
    if (!faqs || faqs.length === 0) {
      return null;
    }
    return faqs.reduce((acc, faq) => {
      if (!acc[faq.categoryName]) {
        acc[faq.categoryName] = [];
      }
      acc[faq.categoryName].push(faq);
      return acc;
    }, {} as Record<string, FaqCategoryType[]>);
  };

  // Filter FAQs based on search term
  const filteredFAQs = productFaqsLoading
    ? []
    : productFaqs?.data?.object?.filter(
        (faq: FaqCategoryType) =>
          faq.title.toLowerCase().startsWith(searchText.toLowerCase()) ||
          faq.categoryName.toLowerCase().startsWith(searchText.toLowerCase())
      ) || [];

  const groupedFAQs = productFaqsLoading
    ? null
    : (filteredFAQs?.length > 0 && groupFAQsByCategory(filteredFAQs)) || null;

  // Function to toggle a category's open/close state
  const toggleCategory = (categoryName: string) => {
    setOpenCategories(
      (prevCategories) =>
        prevCategories.includes(categoryName)
          ? prevCategories?.filter((name) => name !== categoryName) // Remove if exists
          : [...prevCategories, categoryName] // Add if not exists
    );
  };

  // Function to check if a category is open
  const isCategoryOpen = (categoryName: string): boolean => {
    return openCategories.includes(categoryName);
  };

  return (
    <ContentLayout>
      <div className="min-h-[78vh] flex flex-col justify-between gap-10 font-UncutSansVariable">
        <div>
          <p className="text-[#101010] text-[28px] ">
            <span className="font-medium">Hello,</span>{" "}
            <span className="font-bold">{firstName}</span>
          </p>
          <p className="font-UncutSansVariable text-[#0D0D0D80] mt-0 text-base mb-[30px]">
            How can we help you?
          </p>

          <div className="w-full mt-[20px] flex justify-between gap-7 font-UncutSansVariable">
            {productFaqsLoading ? (
              <div className="w-full flex justify-center items-center mt-[40px] h-[200px]">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="w-full max-w-[350px]">
                  <div>
                    <div className="w-full">
                      <CustomInput
                        leftIcon={<Magnifer color="#0D0D0D66" size={16} />}
                        placeholder="Search"
                        leftIconStyle={
                          "top-0 bottom-0 my-auto z-10 flex item-center justify-center flex-col"
                        }
                        name="searchText"
                        type="text"
                        inputContainerStyle={`mb-[6px] w-full relative`}
                        inputStyles="w-full !p-2 !pl-10"
                        control={control}
                      />
                    </div>
                    <p className="px-3 text-[#101010] text-sm font-UncutSansVariable mt-5 mb-2.5">
                      Faq Categories
                    </p>
                  </div>
                  {groupedFAQs &&
                    Object.entries(groupedFAQs).map(([categoryName, faqs]) => (
                      <div key={categoryName} className="w-full box-border">
                        <p
                          onClick={() => toggleCategory(categoryName)}
                          className="p-3 w-full font-medium text-base flex gap-5 items-start cursor-pointer text-[#0D0D0D] hover:text-[#6A6A6A]">
                          <span className="w-full flex-auto">
                            {categoryName}
                          </span>
                          <span
                            className={`w-fit flex transition-all duration-200 ease-in mt-1 ${
                              isCategoryOpen(categoryName)
                                ? "rotate-90"
                                : "-rotate-90"
                            }`}>
                            <RightOutlined className="w-2.5" />
                          </span>
                        </p>
                        <div
                          className={`w-full box-border ${
                            isCategoryOpen(categoryName)
                              ? "bg-[#FAFAFA] rounded-[12px]"
                              : ""
                          }`}>
                          {faqs?.map((faq, index) => (
                            <p
                              key={faq.id}
                              className={`w-full my-0 p-3 cursor-pointer flex justify-between items-start gap-2 rounded-lg hover:text-[#6A6A6A] rounded-b-none text-sm transition-all ease-in duration-300 ${
                                index === faqs.length - 1 ? "" : "border-b"
                              } ${
                                isCategoryOpen(categoryName)
                                  ? "h-fit flex opacity-100"
                                  : "h-0 opacity-0 overflow-hidden hidden"
                              } ${
                                faq?.id === activeFaq?.id
                                  ? "text-[#0D0D0D]"
                                  : " opacity-40 hover:opacity-90 font-medium"
                              }`}
                              onClick={() => setActiveFaq(faq)}>
                              <span className="flex">{faq.title}</span>
                              <span
                                className={`w-fit flex transition-all duration-200 ease-in mt-0.5 ${
                                  faq?.id !== activeFaq?.id ? "rotate-90" : ""
                                }`}>
                                <RightOutlined className="w-2.5" />
                              </span>
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
                {productFaqs?.data?.object?.length === 0 ||
                !productFaqs?.data?.object ||
                !groupedFAQs ? (
                  <div className="w-full flex bg-light rounded-[12px] p-6 h-80 items-center justify-center">
                    <p className="text-xs text-gray-500">No data available!</p>
                  </div>
                ) : (
                  activeFaq &&
                  filteredFAQs.includes(activeFaq) && (
                    <div className="w-full flex-auto bg-light rounded-[12px] p-6">
                      <h4>{activeFaq?.title}</h4>
                      <div
                        dangerouslySetInnerHTML={{ __html: activeFaq?.content }}
                      />
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
        <div className="mb-5">
          <p className="text-xs">Need more help?</p>
          <p
            onClick={() => setIsModalOpen(true)}
            className="text-base font-medium cursor-pointer text-primary underline hover:text-[#6A6A6A] transition-all duration-300 ease-in w-fit">
            Talk to support
          </p>
        </div>
        <FaqModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      </div>
    </ContentLayout>
  );
};

export default Faq;
